/**
 * SurfaceShell — the single page primitive for the rebuilt product.
 * One overline + title + summary sentence + optional KPI strip and actions.
 * Everything else is composition inside the children area.
 */
import { ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { friendlyError } from "./SurfaceState";

interface SurfaceShellProps {
  overline: string;
  title: string;
  summary?: ReactNode;
  actions?: ReactNode;
  kpis?: ReactNode;
  /** Surface-level error banner shown above the body (e.g. primary query failed). */
  error?: unknown;
  onRetry?: () => void;
  children: ReactNode;
  className?: string;
}

export function SurfaceShell({
  overline,
  title,
  summary,
  actions,
  kpis,
  error,
  onRetry,
  children,
  className,
}: SurfaceShellProps) {
  return (
    <div className={cn("flex flex-col min-h-full", className)}>
      <header className="border-b border-border bg-background/60 backdrop-blur-sm">
        <div className="px-6 pt-6 pb-5">
          <div className="flex items-start justify-between gap-6">
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                {overline}
              </div>
              <h1 className="mt-1 text-2xl font-semibold tracking-tight text-foreground">
                {title}
              </h1>
              {summary && (
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed max-w-3xl">
                  {summary}
                </p>
              )}
            </div>
            {actions && (
              <div className="flex flex-none items-center gap-2">{actions}</div>
            )}
          </div>
        </div>
        {kpis && (
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-border border-t border-border">
            {kpis}
          </div>
        )}
      </header>
      {error ? (
        <div
          role="alert"
          className="mx-6 mt-6 rounded border border-destructive/30 bg-destructive/5 px-4 py-3 flex items-start gap-3"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 text-destructive flex-none" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground">Couldn't load this surface</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{friendlyError(error)}</p>
          </div>
          {onRetry && (
            <Button size="sm" variant="outline" className="h-7 text-xs flex-none" onClick={onRetry}>
              <RefreshCw className="h-3 w-3 mr-1.5" /> Try again
            </Button>
          )}
        </div>
      ) : null}
      <div className="flex-1 px-6 py-6">{children}</div>
    </div>
  );
}

interface StatTileProps {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: "default" | "danger" | "warning" | "success" | "info";
  /** When true, render an em-dash placeholder instead of `value`. Use during query loading or error so KPIs reset consistently after retries. */
  loading?: boolean;
}

export function StatTile({ label, value, hint, tone = "default", loading = false }: StatTileProps) {
  const toneClass = {
    default: "text-foreground",
    danger: "text-destructive",
    warning: "text-warning",
    success: "text-success",
    info: "text-info",
  }[tone];
  return (
    <div className="px-6 py-5">
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          "mt-2 text-4xl font-light leading-none tabular-nums",
          loading ? "text-muted-foreground/40" : toneClass,
        )}
        aria-busy={loading || undefined}
      >
        {loading ? "—" : value}
      </div>
      {hint && (
        <div className="mt-2 text-[11px] text-muted-foreground tabular-nums">
          {loading ? "\u00A0" : hint}
        </div>
      )}
    </div>
  );
}
